import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api.js';
import { LIVE_VIEW } from './useIssues.js';
import type { JiraComment } from '@shared/types.js';

export function useComments(issueKey: string) {
  return useQuery({
    queryKey: ['comments', issueKey],
    queryFn: () => api.get<JiraComment[]>(`/api/issues/${issueKey}/comments`).then((r) => r.data),
    enabled: !!issueKey,
    ...LIVE_VIEW,  // comments also come from Jira and Nova
  });
}

export function useAddComment(issueKey: string) {
  const queryClient = useQueryClient();

  return useMutation({
    // Plain text or rich editor ADF
    mutationFn: (input: string | { adf: unknown }) =>
      api.post<JiraComment>(`/api/issues/${issueKey}/comments`, typeof input === 'string' ? { body: input } : input).then((r) => r.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['comments', issueKey] });
      void queryClient.invalidateQueries({ queryKey: ['issue', issueKey] });
      void queryClient.invalidateQueries({ queryKey: ['audit', issueKey] });
    },
  });
}

export function useDeleteComment(issueKey: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (commentId: string) =>
      api.delete(`/api/issues/${issueKey}/comments/${commentId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['comments', issueKey] });
      void queryClient.invalidateQueries({ queryKey: ['issue', issueKey] });
      void queryClient.invalidateQueries({ queryKey: ['audit', issueKey] });
    },
  });
}

export function useEditComment(issueKey: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ commentId, adf }: { commentId: string; adf: unknown }) =>
      api.put(`/api/issues/${issueKey}/comments/${commentId}`, { adf }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['comments', issueKey] });
      void queryClient.invalidateQueries({ queryKey: ['issue', issueKey] });
      void queryClient.invalidateQueries({ queryKey: ['audit', issueKey] });
    },
  });
}
